import { useState } from 'react';
import { XMarkIcon, SparklesIcon } from '@heroicons/react/24/outline';
import { aiService } from '../../services/aiService';
import toast from 'react-hot-toast';

interface AISummaryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  projectName: string;
  onGenerated: (summary: string) => void;
}

export function AISummaryDialog({
  isOpen,
  onClose,
  clientName,
  projectName,
  onGenerated,
}: AISummaryDialogProps) {
  const [observations, setObservations] = useState('');
  const [goals, setGoals] = useState('');
  const [opportunities, setOpportunities] = useState('');
  const [generating, setGenerating] = useState(false);

  if (!isOpen) return null;

  const handleGenerate = async () => {
    if (!observations.trim() || !goals.trim() || !opportunities.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setGenerating(true);
    const toastId = toast.loading('Generating executive summary with AI...');

    try {
      const summary = await aiService.generateExecutiveSummary({
        clientName,
        projectName,
        observations,
        goals,
        opportunities,
      });

      toast.success('Executive summary generated!', { id: toastId });
      onGenerated(summary);
      onClose();
      
      // Reset form
      setObservations('');
      setGoals('');
      setOpportunities('');
    } catch (error) {
      console.error('Failed to generate summary:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate summary';
      toast.error(errorMessage, { id: toastId });
    } finally {
      setGenerating(false);
    }
  };

  const handleClose = () => {
    if (!generating) {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black bg-opacity-30 transition-opacity"
          onClick={handleClose}
        />

        {/* Dialog */}
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-md">
                <SparklesIcon className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900">
                Generate Executive Summary with AI
              </h2>
            </div>
            <button
              onClick={handleClose}
              disabled={generating}
              className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-4 overflow-y-auto max-h-[calc(90vh-140px)]">
            <p className="text-sm text-gray-600 mb-6">
              Provide details about the project to generate a professional executive summary.
              The AI will use Privion's style guide for technical communications.
            </p>

            <div className="space-y-4">
              {/* Client Name (readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client Name
                </label>
                <input
                  type="text"
                  value={clientName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                />
              </div>

              {/* Project Name (readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project Name
                </label>
                <input
                  type="text"
                  value={projectName}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-600"
                />
              </div>

              {/* Observations */}
              <div>
                <label htmlFor="observations" className="block text-sm font-medium text-gray-700 mb-1">
                  Observations (Current Scenario) <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="observations"
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  rows={3}
                  placeholder="Brief notes on current state, challenges, or inefficiencies..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Describe the client's current situation and pain points
                </p>
              </div>

              {/* Goals */}
              <div>
                <label htmlFor="goals" className="block text-sm font-medium text-gray-700 mb-1">
                  Goals <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="goals"
                  value={goals}
                  onChange={(e) => setGoals(e.target.value)}
                  rows={3}
                  placeholder="What the client wants to achieve or improve..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Outline the desired outcomes and improvements
                </p>
              </div>

              {/* Opportunities */}
              <div>
                <label htmlFor="opportunities" className="block text-sm font-medium text-gray-700 mb-1">
                  Opportunities <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="opportunities"
                  value={opportunities}
                  onChange={(e) => setOpportunities(e.target.value)}
                  rows={3}
                  placeholder="How Privion's solution helps, key benefits, or technical differentiators..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Explain how your solution addresses their needs and the value it delivers
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={handleClose}
              disabled={generating}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating || !observations.trim() || !goals.trim() || !opportunities.trim()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <SparklesIcon className={`w-4 h-4 mr-2 ${generating ? 'animate-spin' : ''}`} />
              {generating ? 'Generating...' : 'Generate Summary'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

